/**
 * 공지 팝업의 숨김 기록·추적을 한 곳에서 다룬다.
 *
 * localStorage 키는 예전 모달과 같다. 이미 숨긴 사용자에게 다시 뜨지 않도록 바꾸지 않는다.
 *   텍스트 공지  announcement_{id}_hide_until, 예전 영구 닫기 announcement_{id}_closed
 *   계정 1회     announcement_{id}_account_{uid}_shown
 *   카드뉴스     cardnews_{id}_hide_until, CTA 뒤 영구 cardnews_{id}_dismissed
 */

export const ANNOUNCEMENT_CLOSE_HIDE_DAYS = 1;
export const ANNOUNCEMENT_WEEK_HIDE_DAYS = 7;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const getStoredValue = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setStoredValue = (key, value = 'true') => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // 저장소가 막혀도 닫기 동작은 유지한다. 계정 1회 공지는 서버 claim이 중복을 막는다.
  }
};

const removeStoredValue = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // 저장소가 막혀도 표시 흐름을 막지 않는다.
  }
};

export const isCardNews = (item) => item?.type === 'cardnews';

export const getAnnouncementStorageKey = (announcement, userId = '') => (
  announcement?.oncePerAccount && userId
    ? `announcement_${announcement.id}_account_${userId}_shown`
    : `announcement_${announcement?.id}_closed`
);

export const getAnnouncementHideUntilKey = (announcement) => (
  `announcement_${announcement?.id}_hide_until`
);

export const getCardNewsStorageKeys = (announcement) => ({
  dismissed: `cardnews_${announcement?.id}_dismissed`,
  hideUntil: `cardnews_${announcement?.id}_hide_until`,
});

const getHideUntilKey = (item) => (
  isCardNews(item) ? getCardNewsStorageKeys(item).hideUntil : getAnnouncementHideUntilKey(item)
);

const getPermanentKey = (item) => (
  isCardNews(item) ? getCardNewsStorageKeys(item).dismissed : getAnnouncementStorageKey(item)
);

export const hideAnnouncementForDays = (announcement, days, now = Date.now()) => {
  if (!announcement?.id) return null;
  const hideUntil = new Date(now + days * DAY_IN_MS).toISOString();
  setStoredValue(getHideUntilKey(announcement), hideUntil);
  return hideUntil;
};

const isHiddenUntilLater = (key, now) => {
  const hideUntilValue = getStoredValue(key);
  if (!hideUntilValue) return false;

  const hideUntil = new Date(hideUntilValue).getTime();
  if (!Number.isFinite(hideUntil)) {
    removeStoredValue(key);
    return false;
  }
  if (now < hideUntil) return true;
  removeStoredValue(key);
  return false;
};

/**
 * 이 브라우저 기록 기준으로 공지를 띄워도 되는지. 계정 1회 공지는 로그인 계정이 있어야 하고,
 * 서버 claim은 팝업이 따로 확인한다.
 */
export const isAnnouncementVisible = (item, userId = '', now = Date.now()) => {
  if (!item?.id) return false;
  if (isCardNews(item) && !item.slides?.length) return false;

  if (item.oncePerAccount) {
    if (!userId) return false;
    return getStoredValue(getAnnouncementStorageKey(item, userId)) !== 'true';
  }

  // 예전 영구 닫기 키는 그대로 존중한다. 이미 거절한 사람에게 갑자기 다시 띄우지 않는다.
  if (getStoredValue(getPermanentKey(item)) === 'true') return false;
  return !isHiddenUntilLater(getHideUntilKey(item), now);
};

export const shouldShowAnnouncement = isAnnouncementVisible;

const getDays = (value, fallback) => (Number.isFinite(value) ? value : fallback);

export const getCloseHideDays = (item) => getDays(item?.closeHideDays, ANNOUNCEMENT_CLOSE_HIDE_DAYS);
export const getWeekHideDays = (item) => getDays(item?.dismissDays, ANNOUNCEMENT_WEEK_HIDE_DAYS);

export const markAccountAnnouncementShown = (item, userId) => {
  if (!item?.oncePerAccount || !userId) return;
  setStoredValue(getAnnouncementStorageKey(item, userId));
};

/**
 * 닫기(mode 'close')는 공지별 closeHideDays(기본 1일), 일주일간 안보기(mode 'week')는
 * dismissDays(기본 7일)만큼 숨긴다. 계정 1회 공지는 어느 쪽이든 그 계정에서 다시 뜨지 않는다.
 */
export const hideAnnouncements = (items, mode, userId = '', now = Date.now()) => {
  items.forEach((item) => {
    if (item.oncePerAccount) {
      markAccountAnnouncementShown(item, userId);
      return;
    }
    const days = mode === 'week' ? getWeekHideDays(item) : getCloseHideDays(item);
    hideAnnouncementForDays(item, days, now);
  });
};

/** CTA를 누른 공지: 카드뉴스는 영구 숨김, 텍스트 공지는 닫기 기간만큼 숨긴다. */
export const markPrimaryAction = (item, userId = '', now = Date.now()) => {
  if (item.oncePerAccount) {
    markAccountAnnouncementShown(item, userId);
    return;
  }
  if (isCardNews(item)) {
    setStoredValue(getCardNewsStorageKeys(item).dismissed);
    return;
  }
  hideAnnouncementForDays(item, getCloseHideDays(item), now);
};

const CARD_NEWS_EVENTS = {
  impression: 'cardnews_impression',
  cta: 'cardnews_cta_click',
  dismiss: 'cardnews_dismiss',
  social: 'cardnews_social_click',
};

const ANNOUNCEMENT_CALLBACKS = {
  impression: 'onImpression',
  cta: 'onPrimaryClick',
  dismiss: 'onDismiss',
};

const sendCardNewsEvent = (eventName, item, details) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, {
    event_category: 'Card_News',
    event_label: item.id,
    ...details,
  });
};

/** 노출·클릭·닫기 추적. 추적 실패가 공지 UX를 막지 않게 모든 예외를 삼킨다. */
export const trackAnnouncement = (item, action, details = {}) => {
  try {
    if (isCardNews(item) && CARD_NEWS_EVENTS[action]) {
      sendCardNewsEvent(CARD_NEWS_EVENTS[action], item, details);
    }
    const callback = item?.[ANNOUNCEMENT_CALLBACKS[action]];
    if (typeof callback === 'function') callback();
  } catch {
    // 추적 실패는 무시한다.
  }
};

export const isPrerendering = () => (
  typeof navigator !== 'undefined'
  && /HeadlessChrome|ReactSnap|Prerender/i.test(navigator.userAgent || '')
);
